import { useDebouncedType } from '@apps/admin/hooks/useKeyEvent';
import { useOnClickOutside } from '@hooks/useOnClickOutside';
import { ChevronDownIcon } from '@src/assets';
import React, { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { OptionProps } from './Option';
import { Input } from './styled/Input.styled';
import { Popover } from './styled/Popover.styled';
import * as styled from './styled/Select.styled';

export const Select: React.FC<{
    disabled?: boolean;
    name: string;
    children: React.ReactElement<OptionProps>[];
}> = ({ name, children, disabled = false }) => {
    const { register, setValue } = useFormContext();
    const [open, setOpen] = useState(false);
    const input = useDebouncedType();

    // Auto select a value if it is typed
    useEffect(() => {
        if (!open || !input || disabled) return;
        else if (input === 'Escape' || input === 'Enter') {
            setOpen(false);
            return;
        }

        const index = children.findIndex((x) =>
            x.props.value.toLowerCase().startsWith(input.toLowerCase()),
        );
        if (index >= 0) {
            // Set value of typed option
            const option = children[index];
            setValue(name, option.props.value);

            // Focus of option in UI
            if (dropdownRef.current) {
                const items = dropdownRef.current.getElementsByTagName('li');
                if (items && items.length) {
                    for (let i = 0; i < items.length; i++) {
                        if (items[i].innerHTML === option.props.value) {
                            items[i].scrollIntoView();
                            items[i].setAttribute('tabindex', '0');
                            items[i].focus();
                        }
                    }
                }
            }
        }
    }, [name, input, disabled]);

    const dropdownRef = useRef<HTMLDivElement>(null);
    useOnClickOutside(dropdownRef, () => setOpen(false));

    const onClick = (value: string) => {
        setOpen(false);
        setValue(name, value);
    };

    return (
        <styled.Select ref={dropdownRef}>
            <styled.InputWrapper>
                <Input
                    disabled={disabled}
                    onClick={() => setOpen(!open)}
                    {...register(name)}
                    readOnly
                />
                <ChevronDownIcon />
            </styled.InputWrapper>
            {open && (
                <Popover>
                    {children.map((child) =>
                        React.cloneElement(child, {
                            onClick,
                            ...child.props,
                        }),
                    )}
                </Popover>
            )}
        </styled.Select>
    );
};
